import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { CheckCircle, Clock, MagnifyingGlass, Phone, CreditCard, RocketLaunch, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const STEP_ICONS = {
  1: MagnifyingGlass,
  2: Clock,
  3: Phone,
  4: CreditCard,
  5: RocketLaunch,
};

export default function OnboardingStatus() {
  const [params] = useSearchParams();
  const initialEmail = params.get("email") || "";
  const [email, setEmail] = useState(initialEmail);
  const [inputEmail, setInputEmail] = useState(initialEmail);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStatus = async (e) => {
    if (!e) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/onboarding/status?email=${encodeURIComponent(e)}`);
      setData(data);
    } catch (err) {
      setData(null);
      setError(extractApiError(err, "No onboarding request found for this email."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialEmail) fetchStatus(initialEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="onboarding-status-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" testId="on-logo" />
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-24">
        <div className="text-overline text-primary mb-3">Onboarding progress</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-[1.05]">Track your Orixa activation.</h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-xl">
          Enter the email you submitted to view your current onboarding stage.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); setEmail(inputEmail); fetchStatus(inputEmail); }}
          className="mt-8 flex gap-3 max-w-xl"
          data-testid="on-search-form"
        >
          <input
            type="email"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            placeholder="you@company.com"
            className="flex-1 bg-card border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
            data-testid="on-email-input"
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-primary text-primary-foreground rounded-lg px-5 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            data-testid="on-search-submit"
          >
            {loading ? "Checking…" : "Check status"} <ArrowRight size={14} weight="bold" />
          </button>
        </form>

        {error && (
          <div className="mt-8 bg-card border border-border rounded-xl p-6 text-sm text-muted-foreground" data-testid="on-error">
            {error}
            <div className="mt-3">
              <Link to="/request-access" className="text-primary underline underline-offset-2">Submit a new access request →</Link>
            </div>
          </div>
        )}

        {data && (
          <div className="mt-10 bg-card border border-border rounded-2xl p-6 sm:p-8 card-elev" data-testid="on-result">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-overline text-muted-foreground">Request from</div>
                <div className="font-display text-xl mt-1 tracking-tight">{data.full_name}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{data.company_name}</div>
              </div>
              <div className="text-right">
                <div className="text-overline text-muted-foreground">Reference</div>
                <code className="text-xs bg-secondary px-2.5 py-1 rounded-md mt-1 inline-block" data-testid="on-lead-id">{data.lead_id}</code>
              </div>
            </div>

            <div className="mt-8 space-y-3" data-testid="on-progress-steps">
              {data.progress.steps.map((s) => {
                const Icon = STEP_ICONS[s.n] || Clock;
                const isCurrent = s.n === data.progress.current_step && !s.done;
                const isDone = s.done;
                return (
                  <div
                    key={s.n}
                    className={`flex items-center gap-4 rounded-xl border p-4 transition-all ${
                      isDone ? "bg-teal-soft/50 border-teal/30" : isCurrent ? "bg-card border-primary/40 ring-1 ring-primary/20" : "bg-card border-border"
                    }`}
                    data-testid={`on-step-${s.n}`}
                  >
                    <div className={`h-10 w-10 rounded-lg grid place-items-center flex-shrink-0 ${
                      isDone ? "bg-teal text-white" : isCurrent ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                    }`}>
                      {isDone ? <CheckCircle size={20} weight="fill" /> : <Icon size={20} weight="duotone" />}
                    </div>
                    <div className="flex-1">
                      <div className={`text-sm font-semibold ${isDone ? "text-teal" : "text-foreground"}`}>{s.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {isDone ? "Completed" : isCurrent ? "In progress" : "Upcoming"}
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-muted-foreground">
                      {s.n} / 5
                    </div>
                  </div>
                );
              })}
            </div>

            {data.workspace_id ? (
              <div className="mt-8 bg-teal-soft/30 border border-teal/30 rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-teal">Your workspace is live.</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Check your inbox for the welcome email with login instructions.</div>
                </div>
                <Link
                  to="/login"
                  className="bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
                  data-testid="on-login-link"
                >
                  Sign in <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            ) : (
              <div className="mt-8 text-xs text-muted-foreground text-center leading-relaxed">
                Need to reach us? <a href="mailto:sales@orixa-invest.co?subject=Orixa%20Invest%20-%20Sales%20enquiry" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">Contact sales</a> or email <a href="mailto:support@orixa-invest.co" className="text-primary underline underline-offset-2">support@orixa-invest.co</a>.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
